import React from 'react';
import clsx from 'clsx';
import { GetServerSideProps, NextPageWithLayout } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import CMSPage from '@components/organisms/content-stack/cms-page';
import extractFromContext from '@helpers/utils/extract-from-context';
import ContentStackService from '@services/content-stack-service';
import { EditorialPageData } from '@interfaces/models/editorialPage';
import { ContentStackPageNames } from '@enums/contentStack';
import { DefaultAlternateVersions } from '@components/atoms/head/head';
import Breadcrumbs from '@components/molecules/products/breadcrumbs/breadcrumbs';
import { Breadcrumb } from '@interfaces/models/breadcrumb';
import DefaultLayout from '@layouts/default-layout';
import logger from '@helpers/utils/logger/client';
import { isRequest404Error, isRequestError } from '@helpers/errors';
import { getAppInitialProps } from '@helpers/initial-props';
import styles from './article.module.scss';

type EditoPageProps = {
  contentTypeEntries: EditorialPageData;
};

const EditoPage: NextPageWithLayout<EditoPageProps> = ({ contentTypeEntries }): React.JSX.Element => {
  const { category, categoryUri, categoryTitle, displayTitle, url } = contentTypeEntries || {};
  const alternatives = DefaultAlternateVersions.map((x) => ({
    language: x.language,
    path: url,
  }));
  const breadCrumbs: Breadcrumb[] = [
    { localizedName: 'Journal', url: { path: '/journal/' } },
    {
      localizedName: categoryTitle || category,
      url: {
        path: categoryUri
          ? `/journal/cat/${categoryUri}/`
          : `/journal/cat/${(category || '').replaceAll(' ', '-').toLowerCase()}`,
      },
    },
    { localizedName: displayTitle, url: { path: url } },
  ];

  return (
    <>
      <div className={clsx('innerContainer', styles.edito__breadcrumb)}>
        <Breadcrumbs
          breadcrumbs={breadCrumbs}
          isCatalogPage={false}
          customClass={styles['edito__breadcrumb--list']}
        />
      </div>
      <CMSPage
        contentTypeEntries={contentTypeEntries}
        alternateVersions={alternatives}
      />
    </>
  );
};

EditoPage.Layout = [DefaultLayout];
EditoPage.PageType = 'EditoPage';

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { preferences, locale, userId } = extractFromContext(context);
  const editoSlug = context?.params?.editoSlug as string[];
  try {
    const initialProps = await getAppInitialProps(context);
    const [translations, contentTypeEntries] = await Promise.all([
      serverSideTranslations(locale, ['default-layout', 'common']),
      ContentStackService.getDataForCMSPage(
        ContentStackPageNames.EDITORIAL_PAGE,
        { ...preferences, userId },
        { pageUrl: `/journal/${editoSlug}` },
      ),
    ]);

    return {
      props: {
        ...initialProps,
        ...translations,
        contentTypeEntries: contentTypeEntries || [],
      },
    };
  } catch (err) {
    if (isRequestError(err)) {
      if (!isRequest404Error(err)) {
        logger.error(
          err,
          'There was an api request error on the Edito Page - getServerSideProps - [editoSlug].page.tsx',
        );
      }
      return {
        notFound: true,
      };
    }
    logger.error(err, 'The server failed to render the Edito Page - getServerSideProps - [editoSlug].page.tsx');
    throw err;
  }
};

export default EditoPage;
